import { useState, useEffect, useRef, useCallback } from "react";
import { useParams, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { useTranslation } from "../i18n/I18nContext";
import axios from "axios";
import { Button } from "../components/ui/button";
import { toast } from "sonner";
import { Volume2, VolumeX, Check, X, Loader2, ArrowLeft, Mic, Square } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Avatar image
const TUTOR_AVATAR = "https://customer-assets.emergentagent.com/job_a8bc2321-0709-4088-83ee-4db5665c8646/artifacts/mp07100z_image.png";

// Brand Logo
const LOGO_URL = "https://customer-assets.emergentagent.com/job_b31fc984-f212-4811-9faa-4930fd1aea96/artifacts/xtug1gme_image.png";

// Constants
const RESPONSE_TIMEOUT = 7000; // 7 seconds to respond
const MAX_RECORDING_TIME = 45000; // 45 seconds max recording
const FEEDBACK_DISPLAY_TIME = 2500; // 2.5 seconds to show feedback
const MIN_FEEDBACK_TIME = 1000; // Minimum 1 second feedback

// VAD (Voice Activity Detection) Constants
const VAD_WINDOW_SIZE = 2048; // Samples per analysis window
const VAD_SPEECH_THRESHOLD = 0.04; // Minimum RMS for speech (normalized 0-1)
const VAD_PEAK_THRESHOLD = 0.06; // Minimum peak amplitude for speech
const VAD_VARIANCE_THRESHOLD = 0.001; // Minimum variance for speech pattern
const VAD_MIN_SPEECH_WINDOWS = 3; // Minimum windows with speech detected

// Button states
const BUTTON_STATES = {
  AI_SPEAKING: 'ai_speaking',
  WAITING_TO_RECORD: 'waiting_to_record',
  RECORDING: 'recording',
  PROCESSING: 'processing'
};

export default function PracticeSession() {
  const { sessionId } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const { getAuthHeader } = useAuth();
  const { t } = useTranslation();
  
  // State
  const [isMuted, setIsMuted] = useState(false);
  const [displayedAiMessage, setDisplayedAiMessage] = useState("");
  const [showTranslation, setShowTranslation] = useState(false);
  const [userMessage, setUserMessage] = useState("");
  const [feedbackStatus, setFeedbackStatus] = useState(null);
  const [corrections, setCorrections] = useState([]);
  const [sessionEnded, setSessionEnded] = useState(false);
  const [sessionEnding, setSessionEnding] = useState(false); // New: Session ending state for smooth transition
  const [sessionExpired, setSessionExpired] = useState(false); // 401 Unauthorized handling
  const [currentQuestion, setCurrentQuestion] = useState("");
  const [initialized, setInitialized] = useState(false);
  const [buttonState, setButtonState] = useState(BUTTON_STATES.AI_SPEAKING);
  const [showListeningText, setShowListeningText] = useState(false);
  const [showRetryMessage, setShowRetryMessage] = useState(false); // Tekrar mesajı için
  const [showRepeatCorrectMessage, setShowRepeatCorrectMessage] = useState(false); // Doğru cevabı tekrar et mesajı
  const [timeRemaining, setTimeRemaining] = useState(0); // gerçek süre gelene kadar 0
  const [timerReady, setTimerReady] = useState(false); // backend'den gerçek süre geldi mi
  
  // Refs
  const audioRef = useRef(null);
  const streamRef = useRef(null);
  const mediaRecorderRef = useRef(null);
  const audioChunksRef = useRef([]);
  const responseTimeoutRef = useRef(null);
  const maxRecordingTimeoutRef = useRef(null);
  const currentQuestionRef = useRef("");
  const sessionInitializedRef = useRef(false); // Oturum başlatma kontrolü için
  const timerRef = useRef(null); // Geri sayım timer'ı

  // Update ref when currentQuestion changes
  useEffect(() => {
    currentQuestionRef.current = currentQuestion;
    console.log("currentQuestion updated:", currentQuestion?.substring(0, 80));
  }, [currentQuestion]);

  // Format time as MM:SS
  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  // Geri sayım (yalnızca gerçek süre backend'den geldikten sonra başlar)
  useEffect(() => {
    if (sessionEnded || sessionEnding || !timerReady) return;
    
    timerRef.current = setInterval(() => {
      setTimeRemaining((prev) => {
        if (prev <= 1) {
          // Süre doldu - smooth session ending başlat
          clearInterval(timerRef.current);
          setSessionEnding(true);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
    
    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
    };
  }, [sessionEnded, sessionEnding, timerReady]);

  // Sayfa yenilenme koruması: backend'den gerçek başlangıç zamanını al
  useEffect(() => {
    const syncTimer = async () => {
      if (!sessionId) return;
      try {
        const res = await axios.get(
          `${API}/practice/session/${sessionId}/info`,
          getAuthHeader()
        );
        const startedAt = res.data.started_at;
        const durationMinutes = res.data.session_duration_minutes || 20;
        if (startedAt) {
          const elapsed = Math.floor((Date.now() - new Date(startedAt).getTime()) / 1000);
          const remaining = Math.max(0, durationMinutes * 60 - elapsed);
          setTimeRemaining(remaining);
          setTimerReady(true);
          if (remaining <= 0) {
            setSessionEnding(true);
          }
        }
      } catch (err) {
        console.log("Session info fetch failed, using default timer");
        setTimeRemaining(20 * 60);
        setTimerReady(true);
      }
    };
    syncTimer();
  }, [sessionId]);

  // Heartbeat: açık oturum boyunca ~20 sn'de bir hafif "hâlâ buradayım" ping'i gönder.
  // Backend, yalnızca son ~45 sn içinde sinyal alan dilleri sıcak tutar; öğrenci çıkınca
  // (temiz kapanış, sekme kapatma veya internet kesintisi) ping durur ve o dilin ısıtması
  // ~45 sn içinde kendiliğinden kesilir. Oturum bitince/biterken interval temizlenir.
  useEffect(() => {
    if (!sessionId || sessionEnded || sessionEnding) return;
    let cancelled = false;
    const sendHeartbeat = () => {
      axios.post(
        `${API}/practice/heartbeat`,
        {},
        getAuthHeader()
      ).catch(() => { /* sessiz geç: heartbeat kritik değil */ });
    };
    sendHeartbeat(); // hemen bir kez
    const hb = setInterval(() => { if (!cancelled) sendHeartbeat(); }, 20000);
    return () => {
      cancelled = true;
      clearInterval(hb);
    };
  }, [sessionId, sessionEnded, sessionEnding]); // eslint-disable-line react-hooks/exhaustive-deps

  // Handle session ending - smooth transition
  useEffect(() => {
    if (!sessionEnding) return;
    
    const audio = audioRef.current;
    
    // Function to complete session end with visual fade
    const completeEnd = () => {
      // Wait for CSS fade transition to complete, then navigate
      setTimeout(() => {
        setSessionEnded(true);
      }, 500); // Match CSS transition duration
    };
    
    // Function to start the ending sequence
    const startEndingSequence = () => {
      // If audio is playing, fade it out
      if (audio && !audio.paused && !audio.ended) {
        const fadeInterval = setInterval(() => {
          if (audio.volume > 0.1) {
            audio.volume = Math.max(0, audio.volume - 0.1);
          } else {
            clearInterval(fadeInterval);
            audio.volume = 0;
          }
        }, 50);
      }
      
      // Start visual fade after showing message
      completeEnd();
    };
    
    // Show "Oturum Sona Eriyor" message for 2 seconds, then start fade
    const messageTimeout = setTimeout(startEndingSequence, 2000);
    
    return () => clearTimeout(messageTimeout);
  }, [sessionEnding]);

  // Navigate when session fully ended
  useEffect(() => {
    if (sessionEnded && sessionEnding) {
      // Timer bitti - session'ı sonlandır ve sessions_used'ı artır
      if (sessionId) {
        axios.post(
          `${API}/practice/session/end`,
          { session_id: sessionId },
          getAuthHeader()
        ).then(() => {
          console.log("Session ended successfully via timer, sessions_used incremented");
        }).catch(err => {
          console.error("Error ending session via timer:", err);
        });
      }
      navigate("/dashboard");
    }
  }, [sessionEnded, sessionEnding, navigate, sessionId]);

  // Cleanup function
  const cleanup = useCallback(() => {
    if (responseTimeoutRef.current) clearTimeout(responseTimeoutRef.current);
    if (maxRecordingTimeoutRef.current) clearTimeout(maxRecordingTimeoutRef.current);
    if (mediaRecorderRef.current && mediaRecorderRef.current.state !== 'inactive') {
      try { mediaRecorderRef.current.stop(); } catch (e) {}
    }
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(track => track.stop());
      streamRef.current = null;
    }
  }, []);

  // Handle 401 Unauthorized - Session Expired
  const handleSessionExpired = useCallback(() => {
    console.error("SESSION EXPIRED: 401 Unauthorized detected");
    
    // 1. Stop microphone recording immediately
    if (mediaRecorderRef.current && mediaRecorderRef.current.state !== 'inactive') {
      try { mediaRecorderRef.current.stop(); } catch (e) {}
    }
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(track => track.stop());
      streamRef.current = null;
    }
    
    // 2. Clear all timeouts
    if (responseTimeoutRef.current) clearTimeout(responseTimeoutRef.current);
    if (maxRecordingTimeoutRef.current) clearTimeout(maxRecordingTimeoutRef.current);
    if (timerRef.current) clearInterval(timerRef.current);
    
    // 3. Stop any playing audio
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current.src = '';
    }
    
    // 4. Clear practice session state
    setCurrentQuestion("");
    setShowRetryMessage(false);
    setShowRepeatCorrectMessage(false);
    setShowListeningText(false);
    setButtonState(BUTTON_STATES.AI_SPEAKING);
    
    // 5. Set session expired state - this will show the expiration UI
    setSessionExpired(true);
  }, []);

  // Check if error is 401 Unauthorized
  const is401Error = (error) => {
    return error?.response?.status === 401 || 
           error?.status === 401 ||
           (error?.message && error.message.includes('401'));
  };

  // Initialize session - sadece bir kez çalışmalı
  useEffect(() => {
    // Ref ile kontrol - React Strict Mode ve re-render'lara karşı koruma
    if (sessionInitializedRef.current) return;
    
    const initSession = async () => {
      if (location.state?.initialMessage) {
        // Hemen ref'i true yap - çift çalışmayı önle
        sessionInitializedRef.current = true;
        setInitialized(true);
        
        const text = location.state.initialMessage.text;
        const preloadedAudio = location.state.preloadedAudio;
        console.log("Session initializing with message:", text?.substring(0, 50));
        console.log("Preloaded audio available:", !!preloadedAudio);
        
        setCurrentQuestion(text);
        currentQuestionRef.current = text;
        
        // Use preloaded audio if available (streaming mode), otherwise call TTS
        if (preloadedAudio) {
          console.log("Using preloaded audio - skipping TTS call");
          await speakAIWithAudio(text, preloadedAudio);
        } else {
          await speakAI(text);
        }
      }
    };
    initSession();
    
    return cleanup;
  }, [location.state, cleanup]);

  // Play preloaded audio directly (no TTS API call needed)
  const speakAIWithAudio = async (text, audioBase64) => {
    if (!text || !audioBase64) {
      startWaitingForResponse();
      return;
    }
    
    setButtonState(BUTTON_STATES.AI_SPEAKING);
    setShowListeningText(false);
    setDisplayedAiMessage("");
    
    const shouldShowRepeat = checkIfRepeatRequest(text);
    
    // 1.5 saniye bekle - kullanıcıya hazırlanma süresi ver
    await new Promise(resolve => setTimeout(resolve, 1500));
    
    if (isMuted) {
      setDisplayedAiMessage(text);
      setTimeout(() => {
        startWaitingForResponse(false, shouldShowRepeat);
      }, 1500);
      return;
    }
    
    try {
      if (audioRef.current) {
        audioRef.current.src = `data:audio/mp3;base64,${audioBase64}`;
        
        audioRef.current.onplay = () => {
          setDisplayedAiMessage(text);
        };
        
        audioRef.current.onended = () => {
          startWaitingForResponse(false, shouldShowRepeat);
        };
        
        audioRef.current.onerror = () => {
          setDisplayedAiMessage(text);
          startWaitingForResponse(false, shouldShowRepeat);
        };
        
        await audioRef.current.play();
      }
    } catch (error) {
      console.error("Preloaded audio play error:", error);
      setDisplayedAiMessage(text);
      startWaitingForResponse(false, shouldShowRepeat);
    }
  };

  // Text-to-Speech - AI speaks
  const speakAI = async (text) => {
    if (!text) {
      startWaitingForResponse();
      return;
    }
    
    setButtonState(BUTTON_STATES.AI_SPEAKING);
    setShowListeningText(false);
    setDisplayedAiMessage("");
    
    const shouldShowRepeat = checkIfRepeatRequest(text);
    
    if (isMuted) {
      setDisplayedAiMessage(text);
      setTimeout(() => {
        startWaitingForResponse(false, shouldShowRepeat);
      }, 1500);
      return;
    }
    
    try {
      const response = await axios.post(
        `${API}/practice/tts?text=${encodeURIComponent(text)}&dual_language=${/\[(QUESTION|ANSWER|MNE|ES|EN|DE|IT|PT)\]/i.test(text)}`,
        {},
        getAuthHeader()
      );
      
      if (audioRef.current) {
        audioRef.current.src = `data:audio/mp3;base64,${response.data.audio}`;
        
        audioRef.current.onplay = () => {
          setDisplayedAiMessage(text);
        };
        
        audioRef.current.onended = () => {
          startWaitingForResponse(false, shouldShowRepeat);
        };
        
        audioRef.current.onerror = () => {
          setDisplayedAiMessage(text);
          startWaitingForResponse(false, shouldShowRepeat);
        };
        
        await audioRef.current.play();
      }
    } catch (error) {
      console.error("TTS error:", error);
      // Check for 401 - session expired
      if (is401Error(error)) {
        handleSessionExpired();
        return;
      }
      setDisplayedAiMessage(text);
      startWaitingForResponse(false, shouldShowRepeat);
    }
  };

  // TTS prepare for parallel processing
  const prepareTTS = async (text) => {
    if (!text || isMuted) return null;
    
    try {
      const response = await axios.post(
        `${API}/practice/tts?text=${encodeURIComponent(text)}&dual_language=${/\[(QUESTION|ANSWER|MNE|ES|EN|DE|IT|PT)\]/i.test(text)}`,
        {},
        getAuthHeader()
      );
      return response.data.audio;
    } catch (error) {
      console.error("TTS prepare error:", error);
      // Check for 401 - session expired
      if (is401Error(error)) {
        handleSessionExpired();
        return null;
      }
      return null;
    }
  };

  // Check if AI is asking to repeat
  const checkIfRepeatRequest = (text) => {
    const lowerText = text.toLowerCase();
    return lowerText.includes("repeat") || 
           lowerText.includes("say it again") || 
           lowerText.includes("try again") ||
           lowerText.includes("please say");
  };

  // Play prepared TTS
  const playPreparedTTS = async (audioBase64, text) => {
    setButtonState(BUTTON_STATES.AI_SPEAKING);
    setShowListeningText(false);
    
    const shouldShowRepeat = checkIfRepeatRequest(text);
    
    if (!audioBase64 || isMuted) {
      setDisplayedAiMessage(text);
      setTimeout(() => {
        startWaitingForResponse(false, shouldShowRepeat);
      }, 1500);
      return;
    }
    
    if (audioRef.current) {
      audioRef.current.src = `data:audio/mp3;base64,${audioBase64}`;
      
      audioRef.current.onplay = () => {
        setDisplayedAiMessage(text);
      };
      
      audioRef.current.onended = () => {
        startWaitingForResponse(false, shouldShowRepeat);
      };
      
      audioRef.current.onerror = () => {
        setDisplayedAiMessage(text);
        startWaitingForResponse(false, shouldShowRepeat);
      };
      
      try {
        await audioRef.current.play();
      } catch (e) {
        setDisplayedAiMessage(text);
        startWaitingForResponse(false, shouldShowRepeat);
      }
    }
  };

  // Start waiting for user response (show BAS-KONUŞ button)
  const startWaitingForResponse = (showRetry = false, showRepeatCorrect = false) => {
    console.log("[TRACE-STATE] startWaitingForResponse called, showRetry:", showRetry, "showRepeatCorrect:", showRepeatCorrect);
    cleanup();
    setButtonState(BUTTON_STATES.WAITING_TO_RECORD);
    setShowListeningText(false);
    setShowRetryMessage(showRetry); // Tekrar mesajını göster/gizle
    setShowRepeatCorrectMessage(showRepeatCorrect); // Doğru cevabı tekrar et mesajı
    
    // Start 3.5 second timeout
    responseTimeoutRef.current = setTimeout(() => {
      handleSilenceTimeout();
    }, RESPONSE_TIMEOUT);
  };

  // Handle silence timeout
  const handleSilenceTimeout = async () => {
    console.log("[TRACE-SILENCE] Timeout triggered - user didn't press button, session_id:", sessionId);
    
    cleanup();
    setButtonState(BUTTON_STATES.PROCESSING);
    
    try {
      console.log("[TRACE-API] Sending silence message, current_question:", currentQuestionRef.current?.substring(0, 50));
      const messageResponse = await axios.post(
        `${API}/practice/session/message`,
        {
          session_id: sessionId,
          user_message: "[SILENCE]",
          current_question: currentQuestionRef.current
        },
        getAuthHeader()
      );
      console.log("[TRACE-API] Silence response status:", messageResponse.status);
      
      const { ai_response, next_question } = messageResponse.data;
      
      console.log("[TRACE-SILENCE] AI response:", ai_response?.substring(0, 100));
      
      if (next_question) {
        setCurrentQuestion(next_question);
        currentQuestionRef.current = next_question;
      }
      
      await speakAI(ai_response);
      
    } catch (error) {
      console.error("[TRACE-ERROR] Silence handling error:", error?.response?.status || error.message, error);
      // Check for 401 - session expired
      if (is401Error(error)) {
        handleSessionExpired();
        return;
      }
      await speakAI(t("cant_hear_retry"));
    }
  };

  // Handle button click - toggle recording
  const handleButtonClick = async () => {
    // Block new input if session is ending
    if (sessionEnding) return;
    
    if (buttonState === BUTTON_STATES.WAITING_TO_RECORD) {
      // Start recording
      await startRecording();
    } else if (buttonState === BUTTON_STATES.RECORDING) {
      // Stop recording
      stopRecording();
    }
  };

  // Start recording
  const startRecording = async () => {
    // Block recording if session is ending
    if (sessionEnding) return;
    
    // Clear the response timeout
    if (responseTimeoutRef.current) {
      clearTimeout(responseTimeoutRef.current);
      responseTimeoutRef.current = null;
    }
    
    // Tekrar mesajlarını gizle
    setShowRetryMessage(false);
    setShowRepeatCorrectMessage(false);
    
    try {
      console.log("[TRACE-MIC] Starting microphone...");
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: {
          echoCancellation: true,
          noiseSuppression: true,
          sampleRate: 44100
        }
      });
      streamRef.current = stream;
      console.log("[TRACE-MIC] Microphone stream acquired");
      
      const mimeType = MediaRecorder.isTypeSupported('audio/webm;codecs=opus')
        ? 'audio/webm;codecs=opus'
        : MediaRecorder.isTypeSupported('audio/mp4')
          ? 'audio/mp4'
          : 'audio/webm';
      
      mediaRecorderRef.current = new MediaRecorder(stream, { mimeType });
      audioChunksRef.current = [];
      
      mediaRecorderRef.current.ondataavailable = (event) => {
        if (event.data.size > 0) {
          audioChunksRef.current.push(event.data);
        }
      };
      
      mediaRecorderRef.current.onstop = async () => {
        console.log("[TRACE-MIC] MediaRecorder stopped, chunks:", audioChunksRef.current.length);
        if (audioChunksRef.current.length > 0) {
          const audioBlob = new Blob(audioChunksRef.current, { type: mimeType });
          console.log("[TRACE-AUDIO] Blob created, size:", audioBlob.size, "bytes, empty:", audioBlob.size < 1000);
          if (audioBlob.size > 1000) {
            await processAudio(audioBlob);
          } else {
            console.log("[TRACE-AUDIO] Blob too small, skipping processing");
            startWaitingForResponse();
          }
        } else {
          console.log("[TRACE-AUDIO] No audio chunks recorded");
        }
      };
      
      mediaRecorderRef.current.start(100);
      setButtonState(BUTTON_STATES.RECORDING);
      setShowListeningText(true);
      console.log("[TRACE-MIC] Recording started");
      
      // Set max recording timeout (45 seconds)
      maxRecordingTimeoutRef.current = setTimeout(() => {
        console.log("[TRACE-MIC] Max recording timeout triggered (45s)");
        stopRecording();
      }, MAX_RECORDING_TIME);
      
      // Change button text to "KONUŞMAYI BİTİR" after 1.5 seconds
      // (This is handled by the button state, but we could add a sub-state if needed)
      
    } catch (error) {
      console.error("Microphone error:", error);
      toast.error(t("mic_error"));
      startWaitingForResponse();
    }
  };

  // Stop recording
  const stopRecording = () => {
    console.log("[TRACE-MIC] Stopping recording...");
    if (maxRecordingTimeoutRef.current) {
      clearTimeout(maxRecordingTimeoutRef.current);
      maxRecordingTimeoutRef.current = null;
    }
    
    setShowListeningText(false);
    setButtonState(BUTTON_STATES.PROCESSING);
    
    if (mediaRecorderRef.current && mediaRecorderRef.current.state === 'recording') {
      mediaRecorderRef.current.stop();
      console.log("[TRACE-MIC] MediaRecorder.stop() called");
    } else {
      console.log("[TRACE-MIC] MediaRecorder not in recording state:", mediaRecorderRef.current?.state);
    }
    
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(track => track.stop());
      streamRef.current = null;
      console.log("[TRACE-MIC] Stream tracks stopped");
    }
  };

  // Voice Activity Detection (VAD) - Detect near-field human speech
  // Returns true if speech is detected, false otherwise
  const detectNearFieldSpeech = async (audioBlob) => {
    try {
      const audioContext = new (window.AudioContext || window.webkitAudioContext)();
      const arrayBuffer = await audioBlob.arrayBuffer();
      const audioBuffer = await audioContext.decodeAudioData(arrayBuffer);
      
      // Get audio samples from first channel
      const samples = audioBuffer.getChannelData(0);
      const sampleRate = audioBuffer.sampleRate;
      
      console.log(`VAD: Analyzing ${samples.length} samples at ${sampleRate}Hz`);
      
      let speechWindowCount = 0;
      let totalWindows = 0;
      let maxPeakFound = 0;
      let maxRmsFound = 0;
      
      // Analyze audio in windows
      for (let i = 0; i < samples.length; i += VAD_WINDOW_SIZE) {
        const windowEnd = Math.min(i + VAD_WINDOW_SIZE, samples.length);
        const windowSamples = samples.slice(i, windowEnd);
        
        if (windowSamples.length < VAD_WINDOW_SIZE / 2) continue; // Skip very short final windows
        
        totalWindows++;
        
        // Calculate Peak Amplitude (max absolute value in window)
        let peak = 0;
        for (let j = 0; j < windowSamples.length; j++) {
          const absVal = Math.abs(windowSamples[j]);
          if (absVal > peak) peak = absVal;
        }
        
        // Calculate RMS (Root Mean Square) energy
        let sumSquares = 0;
        for (let j = 0; j < windowSamples.length; j++) {
          sumSquares += windowSamples[j] * windowSamples[j];
        }
        const rms = Math.sqrt(sumSquares / windowSamples.length);
        
        // Calculate Variance (speech has high variance, noise is steady)
        let mean = 0;
        for (let j = 0; j < windowSamples.length; j++) {
          mean += Math.abs(windowSamples[j]);
        }
        mean /= windowSamples.length;
        
        let varianceSum = 0;
        for (let j = 0; j < windowSamples.length; j++) {
          const diff = Math.abs(windowSamples[j]) - mean;
          varianceSum += diff * diff;
        }
        const variance = varianceSum / windowSamples.length;
        
        // Track max values for debugging
        if (peak > maxPeakFound) maxPeakFound = peak;
        if (rms > maxRmsFound) maxRmsFound = rms;
        
        // Check if this window contains speech
        // Near-field speech has: high peak, high RMS, high variance
        const hasSpeech = (
          peak >= VAD_PEAK_THRESHOLD &&
          rms >= VAD_SPEECH_THRESHOLD &&
          variance >= VAD_VARIANCE_THRESHOLD
        );
        
        if (hasSpeech) {
          speechWindowCount++;
        }
      }
      
      // Close audio context
      await audioContext.close();
      
      // Decision: speech detected if enough windows have speech
      const speechDetected = speechWindowCount >= VAD_MIN_SPEECH_WINDOWS;
      
      console.log(`VAD Result: speechWindows=${speechWindowCount}/${totalWindows}, maxPeak=${maxPeakFound.toFixed(3)}, maxRms=${maxRmsFound.toFixed(3)}, detected=${speechDetected}`);
      
      return speechDetected;
      
    } catch (error) {
      console.error("VAD Error:", error);
      // On error, let backend handle it (fail-open for VAD)
      return true;
    }
  };

  // Silence Trim — Removes leading/trailing silence (and long internal silences) before
  // sending audio to Whisper. Reuses the same VAD window analysis to locate speech bounds.
  // Returns a WAV Blob containing only the speech region (with small padding) OR null if
  // no speech was located. On any decoding/encoding error, throws (caller will fall back
  // to the original audio blob).
  const trimAudioToSpeech = async (audioBlob) => {
    const audioContext = new (window.AudioContext || window.webkitAudioContext)();
    try {
      const arrayBuffer = await audioBlob.arrayBuffer();
      const audioBuffer = await audioContext.decodeAudioData(arrayBuffer);
      const samples = audioBuffer.getChannelData(0);
      const sampleRate = audioBuffer.sampleRate;

      // Find first and last window indices that contain speech (same logic as VAD)
      let firstSpeechWindow = -1;
      let lastSpeechWindow = -1;
      let windowIndex = 0;

      for (let i = 0; i < samples.length; i += VAD_WINDOW_SIZE) {
        const windowEnd = Math.min(i + VAD_WINDOW_SIZE, samples.length);
        const windowSamples = samples.slice(i, windowEnd);
        if (windowSamples.length < VAD_WINDOW_SIZE / 2) { windowIndex++; continue; }

        let peak = 0;
        for (let j = 0; j < windowSamples.length; j++) {
          const absVal = Math.abs(windowSamples[j]);
          if (absVal > peak) peak = absVal;
        }
        let sumSquares = 0;
        for (let j = 0; j < windowSamples.length; j++) {
          sumSquares += windowSamples[j] * windowSamples[j];
        }
        const rms = Math.sqrt(sumSquares / windowSamples.length);
        let mean = 0;
        for (let j = 0; j < windowSamples.length; j++) {
          mean += Math.abs(windowSamples[j]);
        }
        mean /= windowSamples.length;
        let varianceSum = 0;
        for (let j = 0; j < windowSamples.length; j++) {
          const diff = Math.abs(windowSamples[j]) - mean;
          varianceSum += diff * diff;
        }
        const variance = varianceSum / windowSamples.length;

        const hasSpeech = (
          peak >= VAD_PEAK_THRESHOLD &&
          rms >= VAD_SPEECH_THRESHOLD &&
          variance >= VAD_VARIANCE_THRESHOLD
        );
        if (hasSpeech) {
          if (firstSpeechWindow === -1) firstSpeechWindow = windowIndex;
          lastSpeechWindow = windowIndex;
        }
        windowIndex++;
      }

      if (firstSpeechWindow === -1) {
        // No speech windows located — return null, caller will send original audio
        return null;
      }

      // Asymmetric padding — keep the start tight (250 ms) but be more generous at the end
      // (500 ms) so soft/fading word endings (e.g. final "l" in "Istanbul", question-tone fades)
      // are not clipped. The user's VAD thresholds reject ambient noise correctly; this extra
      // tail padding compensates for naturally quiet sound tails that fall below those thresholds.
      const paddingSamplesStart = Math.floor(sampleRate * 0.25);
      const paddingSamplesEnd = Math.floor(sampleRate * 0.5);
      const trimStart = Math.max(0, firstSpeechWindow * VAD_WINDOW_SIZE - paddingSamplesStart);
      const trimEnd = Math.min(samples.length, (lastSpeechWindow + 1) * VAD_WINDOW_SIZE + paddingSamplesEnd);
      const trimmedSamples = samples.slice(trimStart, trimEnd);

      // Encode trimmed samples as 16-bit PCM mono WAV
      const wavBlob = encodePCM16WAV(trimmedSamples, sampleRate);
      return wavBlob;
    } finally {
      try { await audioContext.close(); } catch (e) { /* ignore */ }
    }
  };

  // Minimal 16-bit PCM mono WAV encoder. Whisper accepts WAV natively when filename ends in .wav.
  const encodePCM16WAV = (samples, sampleRate) => {
    const numChannels = 1;
    const bitsPerSample = 16;
    const byteRate = sampleRate * numChannels * bitsPerSample / 8;
    const blockAlign = numChannels * bitsPerSample / 8;
    const dataLength = samples.length * 2;
    const buffer = new ArrayBuffer(44 + dataLength);
    const view = new DataView(buffer);

    const writeString = (offset, str) => {
      for (let i = 0; i < str.length; i++) view.setUint8(offset + i, str.charCodeAt(i));
    };

    // RIFF header
    writeString(0, 'RIFF');
    view.setUint32(4, 36 + dataLength, true);
    writeString(8, 'WAVE');
    // fmt chunk
    writeString(12, 'fmt ');
    view.setUint32(16, 16, true);
    view.setUint16(20, 1, true); // PCM
    view.setUint16(22, numChannels, true);
    view.setUint32(24, sampleRate, true);
    view.setUint32(28, byteRate, true);
    view.setUint16(32, blockAlign, true);
    view.setUint16(34, bitsPerSample, true);
    // data chunk
    writeString(36, 'data');
    view.setUint32(40, dataLength, true);
    // PCM samples
    let offset = 44;
    for (let i = 0; i < samples.length; i++) {
      const clipped = Math.max(-1, Math.min(1, samples[i]));
      view.setInt16(offset, clipped < 0 ? clipped * 0x8000 : clipped * 0x7FFF, true);
      offset += 2;
    }

    return new Blob([buffer], { type: 'audio/wav' });
  };

  // Decode ANY recorded blob (webm/opus, mp4, etc.) and re-encode the FULL audio as
  // 16-bit PCM WAV. Whisper reliably accepts WAV, whereas some MediaRecorder webm/opus
  // blobs are rejected by OpenAI with "Invalid file format" (usage seconds: 0).
  const blobToWav = async (audioBlob) => {
    const audioContext = new (window.AudioContext || window.webkitAudioContext)();
    try {
      const arrayBuffer = await audioBlob.arrayBuffer();
      const audioBuffer = await audioContext.decodeAudioData(arrayBuffer);
      const samples = audioBuffer.getChannelData(0);
      return encodePCM16WAV(samples, audioBuffer.sampleRate);
    } finally {
      try { await audioContext.close(); } catch (e) { /* ignore */ }
    }
  };

  // Process recorded audio
  const processAudio = async (audioBlob) => {
    setButtonState(BUTTON_STATES.PROCESSING);
    // NOTE: Emma'nın sorusu (displayedAiMessage) ekranda görünür kalacak.
    // Yeni AI cevabı gelmeden temizlenmez; böylece kullanıcı cevabını yazarken
    // sorunun ne olduğunu da görebilir.
    setFeedbackStatus(null);
    setCorrections([]);
    
    // PRIMARY GUARD: Frontend VAD check BEFORE sending to backend
    const hasSpeech = await detectNearFieldSpeech(audioBlob);
    
    if (!hasSpeech) {
      console.log("VAD: No near-field speech detected, not sending to backend");
      setUserMessage(t("cant_hear"));
      setTimeout(() => setUserMessage(""), 2000);
      startWaitingForResponse(false);
      return;
    }
    
    console.log("VAD: Speech detected, sending to backend");
    
    // Silence Trim (Option A) — Strip leading/trailing silence so Whisper does not waste
    // time on sparse audio. Wrapped in try/catch: if anything fails (e.g. decode error on
    // older Safari), we fall back to sending the ORIGINAL audio blob — no regression.
    let payloadBlob = audioBlob;
    let payloadName = 'recording.webm';
    try {
      const trimmedBlob = await trimAudioToSpeech(audioBlob);
      if (trimmedBlob) {
        console.log(`[TRIM] Audio trimmed: ${audioBlob.size} → ${trimmedBlob.size} bytes`);
        payloadBlob = trimmedBlob;
        payloadName = 'recording.wav';
      } else {
        // No trim bounds located — still re-encode the FULL audio to WAV so we never
        // send a raw webm blob that OpenAI may reject with "Invalid file format".
        console.log("[TRIM] No speech bounds located — converting full audio to WAV");
        const fullWav = await blobToWav(audioBlob);
        if (fullWav) {
          payloadBlob = fullWav;
          payloadName = 'recording.wav';
        }
      }
    } catch (trimError) {
      console.warn("[TRIM] Silence trim failed, converting full audio to WAV:", trimError);
      try {
        const fullWav = await blobToWav(audioBlob);
        if (fullWav) {
          payloadBlob = fullWav;
          payloadName = 'recording.wav';
        }
      } catch (wavError) {
        console.warn("[TRIM] Full WAV conversion also failed, sending original audio:", wavError);
      }
    }
    
    try {
      const formData = new FormData();
      formData.append('file', payloadBlob, payloadName);
      // Forward the active question so the Spanish (es) STT can bias Whisper's language
      // context + run the Azure es-ES referee (same as the standalone). English/Montenegrin ignore it.
      if (currentQuestionRef.current) {
        formData.append('current_question', currentQuestionRef.current);
      }
      
      // STT
      console.log("[TRACE-API] Sending STT request, blob size:", payloadBlob.size, "name:", payloadName);
      const sttResponse = await axios.post(
        `${API}/practice/stt${sessionId ? `?session_id=${encodeURIComponent(sessionId)}` : ''}`,
        formData,
        {
          ...getAuthHeader(),
          headers: { ...getAuthHeader().headers, 'Content-Type': 'multipart/form-data' }
        }
      );
      console.log("[TRACE-API] STT response status:", sttResponse.status);
      
      const transcribedText = sttResponse.data.text;
      const wasFiltered = sttResponse.data.filtered;
      const filterMessage = sttResponse.data.message;
      
      console.log("[TRACE-STT] Result:", transcribedText, "filtered:", wasFiltered, "message:", filterMessage);
      
      if (wasFiltered || !transcribedText || transcribedText.trim().length === 0) {
        console.log("[TRACE-SILENCE] Empty/filtered STT result, reason:", filterMessage || "no transcription");
        // Sessizlik tespit edildiyse kullanıcıya bildir
        if (filterMessage) {
          setUserMessage(filterMessage);
          setTimeout(() => setUserMessage(""), 2000); // 2 saniye sonra temizle
        }
        startWaitingForResponse(false);
        return;
      }
      
      // Tek kelime kontrolü - halüsinasyonu önlemek için
      const wordCount = transcribedText.trim().split(/\s+/).length;
      if (wordCount < 2) {
        console.log("[TRACE-SILENCE] Single word detected:", transcribedText);
        startWaitingForResponse(true); // Tekrar mesajını göster
        return;
      }
      
      setUserMessage(transcribedText);
      
      // Analyze response
      console.log("[TRACE-API] Sending message request, session_id:", sessionId, "current_question:", currentQuestionRef.current?.substring(0, 80));
      
      const messageResponse = await axios.post(
        `${API}/practice/session/message`,
        {
          session_id: sessionId,
          user_message: transcribedText,
          current_question: currentQuestionRef.current
        },
        getAuthHeader()
      );
      console.log("[TRACE-API] Message response status:", messageResponse.status);
      
      const { ai_response, is_correct, is_question, corrections: aiCorrections, next_question } = messageResponse.data;
      
      console.log("[TRACE-API] Analysis result - is_correct:", is_correct, "is_question:", is_question);
      
      // Update currentQuestion
      if (next_question) {
        setCurrentQuestion(next_question);
        currentQuestionRef.current = next_question;
      }
      
      if (is_question) {
        // Student asked a question - no feedback colors
        // Keep user message on screen until AI response appears
        const audioBase64 = await prepareTTS(ai_response);
        setUserMessage("");  // Clear user message right before AI speaks
        await playPreparedTTS(audioBase64, ai_response);
      } else {
        // Show feedback with parallel TTS
        const feedbackStartTime = Date.now();
        
        setFeedbackStatus(is_correct ? 'correct' : 'incorrect');
        setCorrections(aiCorrections || []);
        
        // Prepare TTS in parallel
        const ttsPromise = prepareTTS(ai_response);
        const audioBase64 = await ttsPromise;
        
        // Ensure minimum feedback time
        const elapsed = Date.now() - feedbackStartTime;
        const remainingTime = Math.max(0, MIN_FEEDBACK_TIME - elapsed);
        
        setTimeout(async () => {
          setUserMessage("");
          setFeedbackStatus(null);
          setCorrections([]);
          await playPreparedTTS(audioBase64, ai_response);
        }, remainingTime);
      }
      
    } catch (error) {
      console.error("[TRACE-ERROR] Processing error:", error?.response?.status || error.message, error);
      // Check for 401 - session expired
      if (is401Error(error)) {
        handleSessionExpired();
        return;
      }
      startWaitingForResponse();
    }
  };

  // Go back and end session
  const goBack = async () => {
    cleanup();
    // Oturumu sonlandır ve sessions_used'ı artır
    if (sessionId) {
      try {
        await axios.post(
          `${API}/practice/session/end`,
          { session_id: sessionId },
          getAuthHeader()
        );
        console.log("Session ended successfully, sessions_used incremented");
      } catch (error) {
        console.error("Error ending session:", error);
      }
    }
    navigate("/dashboard");
  };

  // Get button config based on state
  const getButtonConfig = () => {
    switch (buttonState) {
      case BUTTON_STATES.AI_SPEAKING:
        return {
          text: t("emma_speaking"),
          icon: <VolumeX className="w-5 h-5" />,
          className: "bg-gray-600 cursor-not-allowed opacity-70",
          disabled: true
        };
      case BUTTON_STATES.WAITING_TO_RECORD:
        return {
          text: t("press_speak"),
          icon: <Mic className="w-5 h-5" />,
          className: "bg-red-600 hover:bg-red-700 animate-pulse",
          disabled: false
        };
      case BUTTON_STATES.RECORDING:
        return {
          text: t("stop_speaking"),
          icon: <Square className="w-5 h-5" />,
          className: "bg-red-800 hover:bg-red-900",
          disabled: false,
          recording: true
        };
      case BUTTON_STATES.PROCESSING:
        return {
          text: t("please_wait"),
          icon: <Loader2 className="w-5 h-5 animate-spin" />,
          className: "bg-gray-600 cursor-not-allowed opacity-70",
          disabled: true
        };
      default:
        return {
          text: t("press_speak"),
          icon: <Mic className="w-5 h-5" />,
          className: "bg-red-600",
          disabled: false
        };
    }
  };

  const buttonConfig = getButtonConfig();

  return (
    <>
      {/* Session Expired Overlay - 401 Unauthorized */}
      {sessionExpired && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black">
          <div className="text-center p-8 bg-neutral-900 rounded-2xl border border-neutral-700 max-w-md mx-4">
            <div className="w-16 h-16 mx-auto mb-4 bg-red-500/20 rounded-full flex items-center justify-center">
              <svg className="w-8 h-8 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
              </svg>
            </div>
            <p className="text-2xl font-bold text-white mb-2">{t("connection_error")}</p>
            <p className="text-neutral-400 mb-6">{t("restart_session_msg")}</p>
            <Button
              onClick={() => navigate("/dashboard")}
              className="bg-red-600 hover:bg-red-700 text-white px-8 py-3 text-lg font-medium"
              data-testid="restart-session-btn"
            >
              {t("restart_session_btn")}
            </Button>
          </div>
        </div>
      )}

      {/* Session Ending Overlay - Outside main container for visibility */}
      {sessionEnding && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black">
          <div className="text-center animate-pulse">
            <p className="text-4xl font-extrabold text-red-500 mb-3">{t("session_ending")}</p>
            <p className="text-2xl font-bold text-red-400/70">{t("thanks")}</p>
          </div>
        </div>
      )}
      
      <div 
        className={`h-screen flex flex-col bg-black transition-opacity duration-500 ${sessionEnding ? 'opacity-0' : 'opacity-100'}`} 
        data-testid="practice-session"
      >
        <audio ref={audioRef} />
        
      {/* Header */}
      <div className="relative flex items-center justify-between px-4 py-3 bg-neutral-900 border-b border-neutral-800">
        <Button
          variant="ghost"
          size="sm"
          className="text-white/70 hover:text-white hover:bg-white/10"
          onClick={goBack}
          data-testid="back-btn"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          {t("exit_session")}
        </Button>

        {/* Logo — absolutely centered in the header so it stays fixed in the middle
            (between the photo/timer) regardless of the exit-label length across languages */}
        <img
          src={LOGO_URL}
          alt="Talkivist"
          className="h-6 absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 pointer-events-none"
        />

        <Button
          variant="ghost"
          size="sm"
          className="text-white/70 hover:text-white hover:bg-white/10"
          onClick={() => setIsMuted(!isMuted)}
          data-testid="mute-btn"
        >
          {isMuted ? <VolumeX className="w-5 h-5" /> : <Volume2 className="w-5 h-5" />}
        </Button>
      </div>

      {/* AI Section */}
      <div className="flex-1 flex flex-col items-center justify-center bg-black relative">
        {/* Geri Sayım Sayacı */}
        <div className="absolute top-4 left-1/2 -translate-x-1/2 bg-neutral-900 px-4 py-2 rounded-xl border border-neutral-700">
          <div className={`text-2xl font-mono font-bold ${!timerReady ? 'text-neutral-900' : timeRemaining <= 60 ? 'text-red-500 animate-pulse' : timeRemaining <= 300 ? 'text-yellow-500' : 'text-white'}`} data-testid="session-timer">
            {timerReady ? formatTime(timeRemaining) : '--:--'}
          </div>
        </div>
        
        <div className="relative mb-4 mt-12">
          <div className={`w-36 h-36 md:w-44 md:h-44 rounded-full overflow-hidden border-4 ${buttonState === BUTTON_STATES.AI_SPEAKING ? 'border-red-500 ring-4 ring-red-500/30' : 'border-white/20'} transition-all`}>
            <img src={TUTOR_AVATAR} alt="Emma" className="w-full h-full object-cover" />
          </div>
          <div className="absolute -bottom-2 left-1/2 -translate-x-1/2 bg-red-600 text-white px-3 py-1 rounded-full text-sm font-medium">
            Emma
          </div>
        </div>

        <div className="max-w-lg mx-4 min-h-[80px] flex flex-col items-center justify-center px-6 py-3 bg-black/60 backdrop-blur-sm rounded-xl border border-white/10" data-testid="ai-subtitle">
          {displayedAiMessage ? (() => {
            const cleaned = displayedAiMessage
              .replace(/<phoneme[^>]*>([\s\S]*?)<\/phoneme>/gi, '$1')
              .replace(/\[CORRECT\]/g, '').replace(/\[WRONG\]/g, '').replace(/\[IRRELEVANT\]/g, '');
            const tMatch = cleaned.match(/\[TRANSLATION\]([\s\S]*?)\[\/TRANSLATION\]/i);
            const translationText = tMatch ? tMatch[1].trim().replace(/\[\/?QUESTION\]/gi, '').replace(/\[\/?ANSWER\]/gi, '') : '';
            const mainText = cleaned.replace(/\[TRANSLATION\][\s\S]*?\[\/TRANSLATION\]/gi, '').trim();
            return (
              <>
                <p className="text-lg font-medium text-center leading-relaxed text-white">
                  {mainText.split(/(\[ANSWER\].*?\[\/ANSWER\]|\[QUESTION\].*?\[\/QUESTION\]|\[MNE\].*?\[\/MNE\]|\[ES\].*?\[\/ES\]|\[EN\].*?\[\/EN\]|\[DE\].*?\[\/DE\]|\[IT\].*?\[\/IT\]|\[PT\].*?\[\/PT\])/).map((part, index) => {
                    if (part.startsWith('[ANSWER]') && part.endsWith('[/ANSWER]')) {
                      const answer = part.replace('[ANSWER]', '').replace('[/ANSWER]', '');
                      return <span key={index} className="text-emerald-400 font-bold">"{answer}"</span>;
                    }
                    if (part.startsWith('[QUESTION]') && part.endsWith('[/QUESTION]')) {
                      const question = part.replace('[QUESTION]', '').replace('[/QUESTION]', '');
                      return <span key={index} className="text-orange-400 font-bold">{question}</span>;
                    }
                    if ((part.startsWith('[MNE]') && part.endsWith('[/MNE]')) || (part.startsWith('[ES]') && part.endsWith('[/ES]')) || (part.startsWith('[EN]') && part.endsWith('[/EN]')) || (part.startsWith('[DE]') && part.endsWith('[/DE]')) || (part.startsWith('[IT]') && part.endsWith('[/IT]')) || (part.startsWith('[PT]') && part.endsWith('[/PT]'))) {
                      const body = part.replace(/\[\/?(MNE|ES|EN|DE|IT|PT)\]/g, '');
                      return <span key={index} className="text-orange-400 font-semibold">{body}</span>;
                    }
                    return <span key={index}>{part}</span>;
                  })}
                </p>
                {translationText && (
                  <div className="mt-3 flex flex-col items-center gap-2 w-full" data-testid="translation-section">
                    <button
                      type="button"
                      onClick={() => setShowTranslation(v => !v)}
                      className="text-xs text-white/60 hover:text-white/90 underline underline-offset-2 transition-colors"
                      data-testid="toggle-translation-btn"
                    >
                      {showTranslation ? t("hide_translation") : t("show_translation")}
                    </button>
                    {showTranslation && (
                      <p className="text-sm text-white/70 text-center leading-relaxed italic" data-testid="translation-text">
                        {translationText.split(/(\[MNE\].*?\[\/MNE\]|\[ES\].*?\[\/ES\]|\[EN\].*?\[\/EN\]|\[DE\].*?\[\/DE\]|\[IT\].*?\[\/IT\]|\[PT\].*?\[\/PT\])/).map((part, index) => {
                          if ((part.startsWith('[MNE]') && part.endsWith('[/MNE]')) || (part.startsWith('[ES]') && part.endsWith('[/ES]')) || (part.startsWith('[EN]') && part.endsWith('[/EN]')) || (part.startsWith('[DE]') && part.endsWith('[/DE]')) || (part.startsWith('[IT]') && part.endsWith('[/IT]')) || (part.startsWith('[PT]') && part.endsWith('[/PT]'))) {
                            const body = part.replace(/\[\/?(MNE|ES|EN|DE|IT|PT)\]/g, '');
                            return <span key={index} className="text-orange-400 font-semibold not-italic">{body}</span>;
                          }
                          return <span key={index}>{part}</span>;
                        })}
                      </p>
                    )}
                  </div>
                )}
              </>
            );
          })() : buttonState === BUTTON_STATES.PROCESSING ? (
            <Loader2 className="w-6 h-6 text-white animate-spin" />
          ) : null}
        </div>
      </div>

      {/* Divider */}
      <div className="h-1 bg-red-600" />

      {/* User Section */}
      <div className="flex-1 flex flex-col items-center justify-center bg-neutral-900 relative">
        {/* SİZİ DİNLİYORUM text */}
        {showListeningText && (
          <div className="absolute top-6 left-1/2 -translate-x-1/2">
            <p className="text-red-400 text-lg font-semibold animate-pulse">
              {t("listening_to_you")}
            </p>
          </div>
        )}
        
        {/* TEKRAR EDİN - TAM CÜMLE CEVAP VERİN mesajı */}
        {showRetryMessage && buttonState === BUTTON_STATES.WAITING_TO_RECORD && (
          <div className="absolute top-6 left-1/2 -translate-x-1/2 text-center">
            <p className="text-orange-400 text-lg font-semibold">
              {t("repeat_please")}
            </p>
            <p className="text-orange-400/80 text-base font-medium mt-3">
              {t("full_sentence_answer")}
            </p>
          </div>
        )}
        
        {/* Repeat correct answer message */}
        {showRepeatCorrectMessage && buttonState === BUTTON_STATES.WAITING_TO_RECORD && !showRetryMessage && (
          <div className="absolute top-6 left-1/2 -translate-x-1/2 text-center">
            <p className="text-orange-400 text-lg font-semibold">
              {t("repeat_correct")}
            </p>
          </div>
        )}

        {/* User message area */}
        <div className="max-w-lg mx-4 min-h-[80px] flex flex-col items-center justify-center px-6 py-4 mb-4" data-testid="user-subtitle">
          {userMessage && (
            <div className="flex items-center gap-3">
              <p className={`text-xl font-medium ${feedbackStatus === 'correct' ? 'text-emerald-400' : feedbackStatus === 'incorrect' ? 'text-red-400' : 'text-white'}`}>
                {userMessage}
              </p>
              {feedbackStatus === 'correct' && (
                <Check className="w-8 h-8 text-emerald-500" data-testid="correct-icon" />
              )}
              {feedbackStatus === 'incorrect' && (
                <X className="w-8 h-8 text-red-500" data-testid="incorrect-icon" />
              )}
            </div>
          )}
          
          {corrections.length > 0 && (
            <div className="mt-3 text-red-300 text-base text-center">
              {corrections.map((c, i) => <p key={i}>{c}</p>)}
            </div>
          )}
        </div>

        {/* BAS-KONUŞ Button - Always visible */}
        <div className="mb-8">
          <button
            onClick={handleButtonClick}
            disabled={buttonConfig.disabled}
            className={`
              flex items-center justify-center gap-3 
              px-8 py-4 rounded-full 
              text-white font-bold text-lg
              transition-all duration-300 ease-in-out
              ${buttonConfig.className}
              ${buttonConfig.recording ? 'ring-4 ring-red-500/50' : ''}
            `}
            data-testid="record-btn"
          >
            {buttonConfig.icon}
            <span>{buttonConfig.text}</span>
            {buttonConfig.recording && (
              <span className="ml-2 w-3 h-3 bg-red-500 rounded-full animate-pulse" />
            )}
          </button>
        </div>
      </div>
    </div>
    </>
  );
}
